import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { PenLine, LogIn, CheckCircle2, Loader2, FileCheck2, LogOut } from "lucide-react";
import { API } from "@/lib/api";
import { SignaturePad } from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const KEY = "signer_token";
const sApi = axios.create({ baseURL: API });
sApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#0B2545] text-white">
      <header className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
        <img src="/valbrac-logo-3d.png" alt="Valbrac" className="h-10 w-10" />
        <div><p className="text-sm font-extrabold tracking-tight">VALBRAC · Portal de Assinaturas</p>
          <p className="text-[10px] uppercase tracking-wider text-emerald-400">Certificados de Treinamento</p></div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-8">{children}</main>
    </div>
  );
}

export default function SignerPortal() {
  const [phase, setPhase] = useState("loading");
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [signing, setSigning] = useState(null);
  const [sig, setSig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [u, setU] = useState(""); const [p, setP] = useState(""); const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await sApi.get("/assinatura/pending"); setMe({ role: data.role, name: data.name }); setItems(data.items); setPhase("list"); }
    catch { localStorage.removeItem(KEY); setPhase("login"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const login = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await sApi.post("/assinatura/login", { username: u, password: p }); localStorage.setItem(KEY, data.token); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Falha no login"); }
    finally { setBusy(false); }
  };
  const logout = () => { localStorage.removeItem(KEY); setPhase("login"); setItems([]); };

  const doSign = async () => {
    if (!sig) { toast.error("Assine antes de salvar"); return; }
    setSaving(true);
    try { await sApi.post("/assinatura/sign", { enrollment_id: signing.id, signature: sig }); toast.success("Certificado assinado"); setSigning(null); setSig(null); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha ao assinar"); }
    finally { setSaving(false); }
  };

  if (phase === "loading") return <Shell><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div></Shell>;

  return (
    <Shell>
      {phase === "login" ? (
        <div className="mx-auto max-w-sm rounded-2xl bg-white p-6 text-slate-800 shadow-xl" data-testid="signer-login">
          <div className="mb-4 flex items-center gap-2 text-[#0B2545]"><LogIn className="h-5 w-5" /><h2 className="text-lg font-bold">Acesso dos assinantes</h2></div>
          <p className="mb-4 text-xs text-slate-500">Use o login <b>gonzaga</b> ou <b>guilherme</b> e sua senha.</p>
          <form onSubmit={login} className="space-y-3">
            <div><Label className="mb-1 block text-xs font-semibold">Login</Label><Input value={u} onChange={(e) => setU(e.target.value)} data-testid="signer-username" autoFocus /></div>
            <div><Label className="mb-1 block text-xs font-semibold">Senha</Label><Input type="password" value={p} onChange={(e) => setP(e.target.value)} data-testid="signer-password" /></div>
            <Button type="submit" disabled={busy} className="w-full bg-[#0B2545] hover:bg-[#134074]" data-testid="signer-login-btn">{busy ? "Entrando..." : "Entrar"}</Button>
          </form>
        </div>
      ) : (
        <div data-testid="signer-list">
          <div className="mb-4 flex items-center justify-between">
            <div><h2 className="text-lg font-bold">{me?.name}</h2><p className="text-xs text-emerald-400">{items.length} certificado(s) aguardando sua assinatura</p></div>
            <Button variant="ghost" size="sm" onClick={logout} className="text-slate-200 hover:bg-white/10" data-testid="signer-logout"><LogOut className="mr-1 h-4 w-4" /> Sair</Button>
          </div>
          {items.length === 0 ? (
            <div className="rounded-2xl bg-white p-10 text-center text-slate-500 shadow-xl" data-testid="signer-empty"><CheckCircle2 className="mx-auto mb-2 h-10 w-10 text-emerald-500" />Nenhum certificado pendente. Tudo assinado!</div>
          ) : (
            <div className="space-y-2">
              {items.map((it) => (
                <div key={it.id} className="flex items-center justify-between rounded-xl bg-white p-4 text-slate-800 shadow" data-testid={`signer-item-${it.id}`}>
                  <div><p className="text-sm font-bold text-[#0B2545]">{it.employee_name}</p>
                    <p className="text-xs text-slate-500">{it.nr} · {it.course_title} {it.cert_number ? `· Cert. ${it.cert_number}` : ""}</p></div>
                  <Button size="sm" onClick={() => { setSigning(it); setSig(null); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`signer-sign-${it.id}`}><PenLine className="mr-1.5 h-4 w-4" /> Assinar</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!signing} onOpenChange={(o) => !o && setSigning(null)}>
        <DialogContent className="sm:max-w-[560px]" data-testid="signer-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">Assinar certificado</DialogTitle></DialogHeader>
          {signing && <p className="text-sm text-slate-500"><b>{signing.employee_name}</b> — {signing.nr} · {signing.course_title}</p>}
          <SignaturePad value={sig} onChange={setSig} tablet label={me?.name} testid="signer-pad" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSigning(null)}>Cancelar</Button>
            <Button onClick={doSign} disabled={saving} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="signer-save"><FileCheck2 className="mr-1.5 h-4 w-4" />{saving ? "Salvando..." : "Salvar assinatura"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
